import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Collapse,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  PhotoCamera,
  Close as CloseIcon,
} from "@mui/icons-material";
import SignatureCanvas from "react-signature-canvas";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import MDInput from "shared/components/MDInput";
import MDSnackbar from "shared/components/MDSnackbar";

import { useFormJobOrder } from "../../hooks/useFormJobOrder";

function FormJobOrder() {
  const {
    formData,
    actividades,
    setActividades,
    isSubmitting,
    submitError,
    submitSuccess,
    handleChange,
    agregarActividad,
    eliminarActividad,
    handleSubmit,
    setSubmitError,
    setSubmitSuccess,
  } = useFormJobOrder();

  // UI states per-activity
  const [openIndex, setOpenIndex] = useState(null);
  const sigCanvasRefs = useRef({}); // refs per activity: sigCanvasRefs.current[index] => canvas instance
  const [imagenes, setImagenes] = useState({}); // index -> File[]
  const [signatures, setSignatures] = useState({}); // index -> dataURL
  const [savedSignature, setSavedSignature] = useState({}); // index -> boolean
  const [accepted, setAccepted] = useState({}); // index -> boolean (checkbox)
  const [performedDesc, setPerformedDesc] = useState({}); // index -> text
  const [verifier, setVerifier] = useState({}); // index -> string
  const [cargo, setCargo] = useState({}); // index -> string

  // snackbars
  const [openErrorSB, setOpenErrorSB] = useState(false);
  const [openSuccessSB, setOpenSuccessSB] = useState(false);

  useEffect(() => {
    if (submitError) setOpenErrorSB(true);
    if (submitSuccess) setOpenSuccessSB(true);
  }, [submitError, submitSuccess]);

  // Toggle collapse
  const toggleCollapse = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  // Files handling per activity
  const handleImagenesChange = (index, files) => {
    setImagenes((prev) => ({
      ...prev,
      [index]: [...(prev[index] || []), ...Array.from(files)],
    }));
  };

  const handleEliminarImagen = (index, imgIndex) => {
    setImagenes((prev) => ({
      ...prev,
      [index]: prev[index].filter((_, i) => i !== imgIndex),
    }));
  };

  // Signature functions (per activity)
  const saveFirma = (index) => {
    const canvas = sigCanvasRefs.current[index];
    if (!canvas) {
      alert("Canvas no inicializado.");
      return;
    }
    if (canvas.isEmpty()) {
      alert("Por favor firme antes de guardar.");
      return;
    }
    const dataURL = canvas.getTrimmedCanvas().toDataURL("image/png");
    setSignatures((prev) => ({ ...prev, [index]: dataURL }));
    setSavedSignature((prev) => ({ ...prev, [index]: true }));
    // Optionally clear canvas drawing (we'll keep canvas content but hide canvas UI)
    // canvas.clear(); // no es necesario si queremos mantener el preview via dataURL
  };

  const clearFirma = (index) => {
    const canvas = sigCanvasRefs.current[index];
    if (canvas) canvas.clear();
    setSignatures((prev) => {
      const copy = { ...prev };
      delete copy[index];
      return copy;
    });
    setSavedSignature((prev) => ({ ...prev, [index]: false }));
  };

  // Checkbox change per activity
  const handleAcceptedChange = (index, checked) => {
    setAccepted((prev) => ({ ...prev, [index]: checked }));
  };

  // Guardar actividad individual (marca completed y guarda evidencias/firma en el objeto actividad)
  const guardarActividad = (index) => {
    if (!accepted[index]) {
      alert("Debes confirmar que la actividad fue ejecutada (checkbox).");
      return;
    }
    if (!savedSignature[index]) {
      alert("Debe guardar la firma antes de marcar la actividad como completada.");
      return;
    }

    // attach evidences & metadata to the actividad
    setActividades((prev) =>
      prev.map((act, i) => {
        if (i !== index) return act;
        return {
          ...act,
          completed: true,
          evidences: imagenes[index] || [],
          signature: signatures[index] || null,
          verifier: verifier[index] || "",
          cargo: cargo[index] || "",
          performedDescription: performedDesc[index] || act.description || "",
        };
      })
    );

    // mark as completed locally (used for UI)
    setSavedSignature((prev) => ({ ...prev, [index]: true }));
    setOpenIndex(null); // close collapse
  };

  // Helper para "rehacer firma" - permitir sobrescribir la firma guardada
  const redoFirma = (index) => {
    setSavedSignature((prev) => ({ ...prev, [index]: false }));
    setSignatures((prev) => {
      const copy = { ...prev };
      delete copy[index];
      return copy;
    });
    // clear the canvas if exists
    const canvas = sigCanvasRefs.current[index];
    if (canvas) canvas.clear();
  };

  return (
    <Card>
      <MDBox pt={1} px={2} pb={3}>
        <MDTypography variant="body2" fontWeight="regular" color="text">
          <strong>Fecha/Hora Actual:</strong> {new Date().toLocaleString()}
        </MDTypography>
      </MDBox>

      <MDBox component="form" onSubmit={handleSubmit} px={2} pb={3}>
        <Grid container spacing={2}>
          {/* Datos generales */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Fecha y Hora del agendamiento *"
              type="datetime-local"
              name="fechaHora"
              value={formData.fechaHora}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Técnico a cargo *</InputLabel>
              <Select
                name="tecnico"
                value={formData.tecnico}
                onChange={handleChange}
                sx={{ height: 45 }}
              >
                <MenuItem value="tecnico1">Técnico 1</MenuItem>
                <MenuItem value="tecnico2">Técnico 2</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Comercio *</InputLabel>
              <Select
                name="comercio"
                value={formData.comercio}
                onChange={handleChange}
                sx={{ height: 45 }}
              >
                <MenuItem value="cc_santafe">CC Santafe</MenuItem>
                <MenuItem value="cc_puerta_rio">CC Puerta del Rio</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Lista de actividades: cada actividad tiene su propio Collapse / formulario */}
          <Grid item xs={12}>
            {actividades.map((act, index) => (
              <Card key={index} sx={{ mb: 2, p: 2 }}>
                <MDBox display="flex" alignItems="center" justifyContent="space-between">
                  <MDTypography variant="body2">
                    <strong>{act.activity}</strong> - {act.description}
                  </MDTypography>

                  <MDBox display="flex" alignItems="center" gap={1}>
                    {act.completed ? (
                      <CheckCircleIcon color="success" />
                    ) : savedSignature[index] ? (
                      <CheckCircleIcon sx={{ color: "#1976d2" }} />
                    ) : null}
                    <IconButton onClick={() => toggleCollapse(index)}>
                      <ExpandMoreIcon />
                    </IconButton>
                  </MDBox>
                </MDBox>

                {/* Collapse: formulario individual */}
                <Collapse in={openIndex === index} sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    {/* Descripción realizada (editable) */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Descripción de la Actividad realizada"
                        value={performedDesc[index] ?? act.performedDescription ?? ""}
                        onChange={(e) =>
                          setPerformedDesc((prev) => ({ ...prev, [index]: e.target.value }))
                        }
                      />
                    </Grid>

                    {/* Subir evidencias */}
                    <Grid item xs={12}>
                      <MDTypography variant="subtitle2" mb={1}>
                        📷 Subir evidencias
                      </MDTypography>
                      <MDButton
                        component="label"
                        variant="outlined"
                        color="info"
                        fullWidth
                        startIcon={<PhotoCamera />}
                      >
                        {imagenes[index]?.length
                          ? `${imagenes[index].length} imagen(es)`
                          : "Subir imágenes"}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          multiple
                          onChange={(e) => handleImagenesChange(index, e.target.files)}
                        />
                      </MDButton>

                      <MDBox mt={2} display="flex" flexWrap="wrap" gap={2}>
                        {imagenes[index]?.map((img, imgIdx) => (
                          <MDBox
                            key={imgIdx}
                            position="relative"
                            width={100}
                            height={100}
                            sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid #ccc" }}
                          >
                            <img
                              src={URL.createObjectURL(img)}
                              alt={`evidencia-${imgIdx}`}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleEliminarImagen(index, imgIdx)}
                              sx={{
                                position: "absolute",
                                top: 2,
                                right: 2,
                                backgroundColor: "white",
                                "&:hover": { backgroundColor: "#eee" },
                              }}
                            >
                              <CloseIcon fontSize="small" color="error" />
                            </IconButton>
                          </MDBox>
                        ))}
                      </MDBox>
                    </Grid>

                    {/* Datos del verificador */}
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        fullWidth
                        label="Persona que verifica del comercio"
                        value={verifier[index] ?? ""}
                        onChange={(e) =>
                          setVerifier((prev) => ({ ...prev, [index]: e.target.value }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        fullWidth
                        label="Cargo de quien verifica en el comercio"
                        value={cargo[index] ?? ""}
                        onChange={(e) => setCargo((prev) => ({ ...prev, [index]: e.target.value }))}
                      />
                    </Grid>

                    {/* Firma */}
                    <Grid item xs={12}>
                      <MDTypography variant="subtitle2" gutterBottom>
                        ✍️ Firma del Responsable
                      </MDTypography>

                      {/* If signature saved -> show preview + Rehacer firma */}
                      {savedSignature[index] && signatures[index] ? (
                        <MDBox display="flex" flexDirection="column" gap={2}>
                          <MDBox
                            sx={{ border: "1px solid #ccc", borderRadius: 2, overflow: "hidden" }}
                          >
                            <img
                              src={signatures[index]}
                              alt={`firma-${index}`}
                              style={{ width: "100%", height: 150, objectFit: "contain" }}
                            />
                          </MDBox>

                          <MDBox display="flex" gap={2}>
                            <MDButton
                              variant="outlined"
                              color="warning"
                              onClick={() => redoFirma(index)}
                            >
                              Rehacer firma
                            </MDButton>
                          </MDBox>
                        </MDBox>
                      ) : (
                        <>
                          <MDBox
                            sx={{
                              border: "1px solid #ccc",
                              borderRadius: 2,
                              width: "100%",
                              height: 150,
                              mb: 2,
                            }}
                          >
                            <SignatureCanvas
                              ref={(el) => (sigCanvasRefs.current[index] = el)}
                              penColor="black"
                              canvasProps={{
                                width: 500,
                                height: 150,
                                className: "sigCanvas",
                                style: { width: "100%", height: "100%" },
                              }}
                            />
                          </MDBox>

                          <MDBox display="flex" justifyContent="space-evenly" gap={2} mb={2}>
                            <MDButton
                              variant="outlined"
                              color="warning"
                              onClick={() => {
                                const c = sigCanvasRefs.current[index];
                                if (c) c.clear();
                              }}
                            >
                              Limpiar
                            </MDButton>
                            <MDButton
                              variant="contained"
                              color="info"
                              onClick={() => {
                                const c = sigCanvasRefs.current[index];
                                if (!c || c.isEmpty()) {
                                  alert("Por favor firma antes de guardar");
                                  return;
                                }
                                const dataURL = c.getTrimmedCanvas().toDataURL("image/png");
                                setSignatures((prev) => ({ ...prev, [index]: dataURL }));
                                setSavedSignature((prev) => ({ ...prev, [index]: true }));
                              }}
                            >
                              Guardar Firma
                            </MDButton>
                          </MDBox>
                        </>
                      )}
                    </Grid>

                    {/* Confirmación checkbox */}
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!accepted[index]}
                            onChange={(e) => handleAcceptedChange(index, e.target.checked)}
                            color="success"
                          />
                        }
                        label="Confirmo que las actividades fueron ejecutadas y doy por enterado."
                        sx={{ mt: 1 }}
                      />
                    </Grid>

                    {/* Guardar actividad (individual) */}
                    <Grid item xs={12}>
                      <MDButton
                        variant="contained"
                        color="success"
                        fullWidth
                        onClick={() => guardarActividad(index)}
                      >
                        Guardar Actividad
                      </MDButton>
                    </Grid>
                  </Grid>
                </Collapse>
              </Card>
            ))}
          </Grid>

          {/* Guardar agendamiento completo */}
          <Grid item xs={12}>
            <MDButton
              type="submit"
              variant="contained"
              color="info"
              fullWidth
              disabled={isSubmitting}
            >
              {isSubmitting ? "Guardando..." : "Guardar Agendamiento Completo"}
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>

      {/* Snackbars */}
      <MDSnackbar
        color="error"
        icon="warning"
        title="Error"
        content={submitError || ""}
        open={openErrorSB}
        onClose={() => {
          setOpenErrorSB(false);
          setSubmitError(null);
        }}
        close={() => {
          setOpenErrorSB(false);
          setSubmitError(null);
        }}
        bgWhite
      />

      <MDSnackbar
        color="success"
        icon="check"
        title="Éxito"
        content="¡Agendamiento enviado correctamente!"
        open={openSuccessSB}
        onClose={() => {
          setOpenSuccessSB(false);
          setSubmitSuccess(false);
        }}
        close={() => {
          setOpenSuccessSB(false);
          setSubmitSuccess(false);
        }}
        bgWhite
      />
    </Card>
  );
}

export default FormJobOrder;
